import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Star, Calendar, TrendingUp, Grid, ArrowRight } from 'lucide-react';

const CARD_CONFIG = {
  forgotten_gem: {
    icon: Star,
    color: 'amber',
    gradient: 'from-amber-500 to-orange-600',
    bgGradient: 'from-amber-50 to-orange-50',
  },
  last_month_highlights: {
    icon: Calendar,
    color: 'violet',
    gradient: 'from-violet-500 to-purple-600',
    bgGradient: 'from-violet-50 to-purple-50',
  },
  focus_heating_up: {
    icon: TrendingUp,
    color: 'emerald',
    gradient: 'from-emerald-500 to-teal-600',
    bgGradient: 'from-emerald-50 to-teal-50',
  },
  group_together: {
    icon: Grid,
    color: 'blue',
    gradient: 'from-blue-500 to-cyan-600',
    bgGradient: 'from-blue-50 to-cyan-50',
  },
};

export default function MemorySurfacingModal({ card, onClose }) {
  if (!card) return null;

  const config = CARD_CONFIG[card.type] || CARD_CONFIG.forgotten_gem;
  const IconComponent = config.icon;

  return (
    <AnimatePresence>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden pointer-events-auto flex flex-col"
        >
          {/* Header */}
          <div className={`p-6 bg-gradient-to-br ${config.bgGradient}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div
                  className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${config.gradient} flex items-center justify-center shadow-lg`}
                >
                  <IconComponent size={28} className="text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-1">
                    {card.title}
                  </h2>
                  <p className="text-sm text-gray-600 font-medium">
                    {card.subtitle}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/50 rounded-lg transition-colors"
              >
                <X size={20} className="text-gray-600" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Why This Matters */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Why This Matters
              </h3>
              <p className="text-base text-gray-800 leading-relaxed">
                {card.reason}
              </p>
            </div>

            {/* Action Step */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                What You Can Do
              </h3>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <p className="text-base text-gray-800 font-medium">
                  {card.action}
                </p>
              </div>
            </div>

            {/* Metadata (if available) */}
            {card.metadata && (
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Pattern Details
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {card.metadata.daysAgo && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs text-gray-500 mb-1">Days Ago</div>
                      <div className="text-lg font-bold text-gray-900">
                        {card.metadata.daysAgo}
                      </div>
                    </div>
                  )}
                  {card.metadata.itemCount && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs text-gray-500 mb-1">Items</div>
                      <div className="text-lg font-bold text-gray-900">
                        {card.metadata.itemCount}
                      </div>
                    </div>
                  )}
                  {card.metadata.thisWeekCount && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs text-gray-500 mb-1">This Week</div>
                      <div className="text-lg font-bold text-gray-900">
                        {card.metadata.thisWeekCount}
                      </div>
                    </div>
                  )}
                  {card.metadata.lastMonthCount && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs text-gray-500 mb-1">Last Month</div>
                      <div className="text-lg font-bold text-gray-900">
                        {card.metadata.lastMonthCount}
                      </div>
                    </div>
                  )}
                  {card.metadata.recentCount && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs text-gray-500 mb-1">Recent</div>
                      <div className="text-lg font-bold text-gray-900">
                        {card.metadata.recentCount}
                      </div>
                    </div>
                  )}
                  {card.metadata.dormantCount && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs text-gray-500 mb-1">Earlier</div>
                      <div className="text-lg font-bold text-gray-900">
                        {card.metadata.dormantCount}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Keywords */}
                {card.metadata.keywords && card.metadata.keywords.length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs text-gray-500 mb-2">Key Topics</div>
                    <div className="flex flex-wrap gap-2">
                      {card.metadata.keywords.map((keyword, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-white rounded-full text-sm text-gray-700 border border-gray-200"
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {card.metadata.commonKeywords && card.metadata.commonKeywords.length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs text-gray-500 mb-2">Common Themes</div>
                    <div className="flex flex-wrap gap-2">
                      {card.metadata.commonKeywords.map((keyword, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-white rounded-full text-sm text-gray-700 border border-gray-200"
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="p-6 bg-gray-50 border-t border-gray-200">
            <button
              onClick={onClose}
              className={`w-full py-3 px-4 bg-gradient-to-r ${config.gradient} text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2`}
            >
              <span>Got It</span>
              <ArrowRight size={18} />
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}





